import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Checkbox } from '@rudi/ui/checkbox';
import { toast } from 'sonner';
import { QueryState } from '../components/page';
import {
  useAcceptLinkCandidates,
  useDecideLinkCandidate,
  useLinkCandidates,
  useLinkMention,
} from '../api/hooks';
import type {
  IdentityCandidate,
  IdentityCandidateParams,
  IdentityCandidateSide,
  IdentityCandidateSort,
  IdentityLevel,
  IdentityMention,
  IdentitySuggestion,
  IdentityVerdict,
} from '../api/types';
import { DataQualityHeader } from './DataQualityOverviewPage';
import {
  EntityLink,
  EntityPickerDialog,
  LEVEL_NAME,
  Labelled,
  MentionPlace,
  Pager,
  ReasonField,
  control,
  humanise,
  identityErrorText,
  loadError,
  placeText,
  postalCity,
  scoreText,
} from './medical-data-quality';

const QUEUE_SIZE = 25;

type StatusFilter = NonNullable<IdentityCandidateParams['status']>;

const STATUSES: { value: StatusFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'unsure', label: 'Skipped as unsure' },
  { value: 'same', label: 'Decided same' },
  { value: 'not_same', label: 'Decided not the same' },
];

const SORTS: { value: IdentityCandidateSort; label: string }[] = [
  { value: 'score', label: 'Highest score' },
  { value: 'ai_confidence', label: 'AI confidence' },
  { value: 'created', label: 'Newest' },
];

const VERDICT_TEXT: Record<IdentityVerdict, string> = {
  same: 'same',
  not_same: 'not the same',
  unsure: 'unsure',
};

const DONE: Record<IdentityVerdict, string> = {
  same: 'Linked as the same',
  not_same: 'Kept apart',
  unsure: 'Skipped as unsure',
};

function decidable(candidate: IdentityCandidate): boolean {
  return candidate.status === 'open' || candidate.status === 'unsure';
}

/** Candidates the AI calls the same, which bulk accept may take together. */
function acceptable(candidate: IdentityCandidate): boolean {
  return decidable(candidate) && candidate.suggestion?.verdict === 'same';
}

function sideName(side: IdentityCandidateSide | null, mention: IdentityMention | null): string {
  if (side && !side.missing) return side.display_name;
  if (!side) return mention?.organisation_name ?? mention?.person_name ?? 'the Mention';
  return 'a removed entity';
}

export function candidateLabel(candidate: IdentityCandidate): string {
  return `${sideName(candidate.left, candidate.mention)} and ${sideName(candidate.right, null)}`;
}

/**
 * Link Candidates side by side with the Mention that raised them, the evidence
 * and the AI's suggestion. Reviewers decide; the AI never changes a link.
 */
export function ReviewQueuePage() {
  const [params, setParams] = useState<IdentityCandidateParams>({
    status: 'open',
    sort: 'score',
    limit: QUEUE_SIZE,
    offset: 0,
  });
  const candidates = useLinkCandidates(params);
  const accept = useAcceptLinkCandidates();
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkErrors, setBulkErrors] = useState<Record<string, { label: string; text: string }>>({});
  const items = candidates.data?.items ?? [];
  const eligible = items.filter(acceptable);
  const update = (next: IdentityCandidateParams) => {
    setParams(previous => ({ ...previous, ...next, offset: 0 }));
    setSelected([]);
  };

  const acceptSelected = async () => {
    const labels = Object.fromEntries(items.map(item => [item.id, candidateLabel(item)]));
    const ids = selected;
    try {
      const outcomes = await accept.mutateAsync(ids);
      const failed = outcomes.filter(outcome => outcome.error);
      setBulkErrors(
        Object.fromEntries(
          failed.map(outcome => [
            outcome.id,
            {
              label: labels[outcome.id] ?? outcome.id,
              text: identityErrorText(outcome.error, 'Could not accept'),
            },
          ])
        )
      );
      setSelected(failed.map(outcome => outcome.id));
      const accepted = outcomes.length - failed.length;
      if (failed.length === 0) toast.success(`Accepted ${accepted} Link Candidates as the same`);
      else toast.error(`Accepted ${accepted} of ${outcomes.length}. See the errors below.`);
    } catch (error) {
      toast.error(identityErrorText(error, 'Bulk accept failed'));
    }
  };

  return (
    <div>
      <DataQualityHeader />
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-3xl border bg-card p-4">
        <Labelled label="Level">
          <select
            aria-label="Level"
            className={control}
            value={params.kind ?? ''}
            onChange={event =>
              update({ kind: (event.target.value || undefined) as IdentityLevel | undefined })
            }
          >
            <option value="">All levels</option>
            {(Object.keys(LEVEL_NAME) as IdentityLevel[]).map(level => (
              <option key={level} value={level}>
                {LEVEL_NAME[level].many}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Status">
          <select
            aria-label="Status"
            className={control}
            value={params.status}
            onChange={event => update({ status: event.target.value as StatusFilter })}
          >
            {STATUSES.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Sort by">
          <select
            aria-label="Sort by"
            className={control}
            value={params.sort}
            onChange={event => update({ sort: event.target.value as IdentityCandidateSort })}
          >
            {SORTS.map(sort => (
              <option key={sort.value} value={sort.value}>
                {sort.label}
              </option>
            ))}
          </select>
        </Labelled>
      </div>

      {eligible.length > 0 && (
        <div
          aria-label="Bulk accept"
          role="region"
          className="mb-4 flex flex-wrap items-center gap-3 rounded-3xl border bg-card p-4 text-sm"
        >
          <span className="flex-1">
            The AI calls {eligible.length} of these the same. Select them to accept them together;
            each gets its own decision.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelected(eligible.map(item => item.id))}
          >
            Select all the AI calls the same
          </Button>
          <Button
            size="sm"
            disabled={selected.length === 0 || accept.isPending}
            onClick={() => void acceptSelected()}
          >
            {accept.isPending ? 'Accepting…' : `Accept ${selected.length} selected as the same`}
          </Button>
        </div>
      )}
      {Object.keys(bulkErrors).length > 0 && (
        <div
          role="alert"
          className="mb-4 rounded-3xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
        >
          <div className="mb-1 font-medium">Some Link Candidates were not accepted</div>
          <ul className="list-disc space-y-1 pl-5">
            {Object.entries(bulkErrors).map(([id, error]) => (
              <li key={id}>
                {error.label}: {error.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      <QueryState isLoading={candidates.isLoading} error={loadError(candidates.error)}>
        {items.length === 0 ? (
          <p className="rounded-3xl border p-6 text-sm text-muted-foreground">
            {params.status === 'open'
              ? 'No open Link Candidates. The resolver found nothing it could not settle.'
              : 'No Link Candidates with this status.'}
          </p>
        ) : (
          <div className="space-y-4">
            {items.map(candidate => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                selectable={acceptable(candidate)}
                selected={selected.includes(candidate.id)}
                onSelect={checked =>
                  setSelected(previous =>
                    checked
                      ? [...previous, candidate.id]
                      : previous.filter(item => item !== candidate.id)
                  )
                }
                bulkError={bulkErrors[candidate.id]?.text}
              />
            ))}
          </div>
        )}
        {candidates.data && (
          <Pager
            total={candidates.data.total}
            limit={candidates.data.limit}
            offset={candidates.data.offset}
            onChange={offset => {
              setParams(previous => ({ ...previous, offset }));
              setSelected([]);
            }}
          />
        )}
      </QueryState>
    </div>
  );
}

function CandidateCard({
  candidate,
  selectable,
  selected,
  onSelect,
  bulkError,
}: {
  candidate: IdentityCandidate;
  selectable: boolean;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  bulkError?: string;
}) {
  const decide = useDecideLinkCandidate();
  const link = useLinkMention();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const label = candidateLabel(candidate);
  const right = candidate.right && !candidate.right.missing ? candidate.right : null;
  // "Move" links the Mention to another Catalog Site of the Organisation, such
  // as the one at its address, instead of the campus the text names.
  const mention = candidate.mention;
  const canMove =
    candidate.kind === 'site' && right?.kind === 'site' && !!mention && decidable(candidate);
  const addressSiteId = candidate.evidence.address_site_id;
  const addressSite =
    typeof addressSiteId === 'string' && addressSiteId !== right?.id
      ? [
          {
            id: addressSiteId,
            label:
              typeof candidate.evidence.address_site === 'string'
                ? candidate.evidence.address_site
                : 'Catalog Site at the address',
            note: "at the Mention's address",
          },
        ]
      : [];

  const onDecide = async (verdict: IdentityVerdict) => {
    setError(null);
    try {
      await decide.mutateAsync({ id: candidate.id, verdict, reason });
      toast.success(DONE[verdict]);
    } catch (failure) {
      setError(identityErrorText(failure, 'The decision failed'));
    }
  };

  return (
    <article
      aria-label={`Link Candidate: ${label}`}
      className="space-y-4 rounded-3xl border bg-card p-4"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {selectable && (
          <Checkbox
            aria-label={`Select ${label}`}
            checked={selected}
            onCheckedChange={checked => onSelect(checked === true)}
          />
        )}
        <Badge variant="secondary">{LEVEL_NAME[candidate.kind].one}</Badge>
        <span className="font-medium">{humanise(candidate.rule)}</span>
        <span className="text-muted-foreground">score {scoreText(candidate.score)}</span>
        <span className="text-muted-foreground">
          · raised {new Date(candidate.created_at).toLocaleDateString()}
        </span>
        {candidate.status !== 'open' && (
          <Badge variant="outline">{humanise(candidate.status)}</Badge>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {candidate.left === null ? (
          <MentionSide mention={candidate.mention} />
        ) : (
          <Side title="Left" side={candidate.left} kind={candidate.kind} />
        )}
        <Side title="Right" side={candidate.right} kind={candidate.kind} />
      </div>

      {candidate.left !== null && candidate.mention && (
        <div className="text-sm">
          <div className="text-xs text-muted-foreground">Raised by the Mention</div>
          <MentionLine mention={candidate.mention} />
        </div>
      )}

      <Evidence evidence={candidate.evidence} />
      <Suggestion suggestion={candidate.suggestion} />

      {decidable(candidate) ? (
        <div className="space-y-2">
          <ReasonField value={reason} onChange={setReason} />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={decide.isPending}
              aria-label={`Same: ${label}`}
              onClick={() => void onDecide('same')}
            >
              Same
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={decide.isPending}
              aria-label={`Not the same: ${label}`}
              onClick={() => void onDecide('not_same')}
            >
              Not the same
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={decide.isPending || candidate.status === 'unsure'}
              aria-label={`Skip as unsure: ${label}`}
              onClick={() => void onDecide('unsure')}
            >
              Skip (unsure)
            </Button>
            {canMove && (
              <Button
                size="sm"
                variant="outline"
                aria-label={`Move to another Catalog Site: ${label}`}
                onClick={() => setMoving(true)}
              >
                Move to another Catalog Site…
              </Button>
            )}
          </div>
        </div>
      ) : candidate.status === 'superseded' ? (
        <p className="text-sm text-muted-foreground">
          Superseded: a reviewer linked the Mention by hand
          {candidate.decided_by && ` (${candidate.decided_by})`}.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Decided {VERDICT_TEXT[candidate.status as IdentityVerdict] ?? candidate.status}
          {candidate.decided_by && ` by ${candidate.decided_by}`}
          {candidate.decided_at && ` on ${new Date(candidate.decided_at).toLocaleString()}`}.
        </p>
      )}
      {(error || bulkError) && (
        <p role="alert" className="text-sm text-destructive">
          {error ?? bulkError}
        </p>
      )}
      {canMove && (
        <EntityPickerDialog
          open={moving}
          onClose={() => setMoving(false)}
          level="site"
          title="Move the Mention to another Catalog Site"
          description={`Choose the Catalog Site of ${right.organisation_name ?? 'this Organisation'} where the Mention belongs, instead of ${right.display_name}. Every Mention written the same way follows, and this Link Candidate is settled.`}
          confirmLabel="Move"
          filter={{ organisation_id: right.organisation_id }}
          excludeIds={[right.id]}
          pinned={addressSite}
          onConfirm={async (siteId, moveReason) => {
            if (!siteId) return;
            await link.mutateAsync({
              mentionId: mention.id,
              level: 'site',
              entityId: siteId,
              reason: moveReason,
            });
            toast.success('Mention moved to the chosen Catalog Site');
          }}
        />
      )}
    </article>
  );
}

function MentionLine({ mention }: { mention: IdentityMention }) {
  return (
    <div>
      {[
        mention.person_name,
        mention.organisation_name,
        mention.department,
        placeText([mention.address, postalCity(mention.postal_code, mention.city)]),
        mention.email,
      ]
        .filter(Boolean)
        .join(' · ')}
      <span className="text-muted-foreground"> · {mention.registry_identifier}</span>
    </div>
  );
}

function MentionSide({ mention }: { mention: IdentityMention | null }) {
  return (
    <section aria-label="Left" className="rounded-3xl border p-3 text-sm">
      <div className="mb-1 text-xs text-muted-foreground">
        The Mention, not yet at a Catalog Site
      </div>
      {mention ? (
        <>
          <MentionPlace mention={mention} />
          <div className="mt-1 text-xs text-muted-foreground">
            {mention.person_name ?? 'No investigator'} · {mention.registry_identifier}
          </div>
        </>
      ) : (
        <p className="text-muted-foreground">The Mention is no longer in the registry.</p>
      )}
    </section>
  );
}

function Side({
  title,
  side,
  kind,
}: {
  title: string;
  side: IdentityCandidateSide | null;
  kind: IdentityLevel;
}) {
  return (
    <section aria-label={title} className="rounded-3xl border p-3 text-sm">
      {!side || side.missing ? (
        <p className="text-muted-foreground">
          This {LEVEL_NAME[kind].one} no longer exists. It was merged elsewhere; reload the queue.
        </p>
      ) : (
        <>
          <div className="font-medium">
            <EntityLink kind={side.kind} id={side.id}>
              {side.display_name}
            </EntityLink>
            {side.status !== 'active' && (
              <Badge variant="outline" className="ml-2">
                {side.status}
              </Badge>
            )}
          </div>
          <SideFacts side={side} />
          <div className="mt-1 text-xs text-muted-foreground">{side.mention_count} Mentions</div>
          {side.sample_mentions.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {side.sample_mentions.map(mention => (
                <li key={mention.id}>
                  <MentionLine mention={mention} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function SideFacts({ side }: { side: Exclude<IdentityCandidateSide, { missing: true }> }) {
  const line = (text: string) => <div className="text-xs text-muted-foreground">{text}</div>;
  if (side.kind === 'organisation')
    return (
      <>
        {line(placeText([side.city, side.country]) || 'No city')}
        {side.external_ids.length > 0 && line(`OMS ${side.external_ids.join(', ')}`)}
        {side.email_domains.length > 0 && line(`Email domains ${side.email_domains.join(', ')}`)}
      </>
    );
  if (side.kind === 'site')
    return (
      <>
        {side.organisation_name && line(side.organisation_name)}
        {line(placeText([side.street, postalCity(side.postal_code, side.city)]) || 'No address')}
        {side.head_office && line('Head office')}
      </>
    );
  if (side.kind === 'department')
    return (
      <>
        {line(side.site_name ?? 'No Catalog Site')}
        {side.specialty_code && line(`Specialty ${side.specialty_code}`)}
      </>
    );
  return (
    <>
      {(side.organisations ?? []).length > 0 && line((side.organisations ?? []).join(', '))}
      {(side.spellings ?? []).length > 0 && line(`Written ${(side.spellings ?? []).join(', ')}`)}
      {(side.emails ?? []).length > 0 && line((side.emails ?? []).join(', '))}
    </>
  );
}

function factText(value: unknown): string {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (typeof value === 'object') return JSON.stringify(value);
  return humanise(String(value));
}

function Evidence({ evidence }: { evidence: Record<string, unknown> }) {
  const facts = Object.entries(evidence ?? {});
  if (facts.length === 0) return null;
  return (
    <section aria-label="Evidence" className="text-sm">
      <div className="mb-1 text-xs text-muted-foreground">Evidence</div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        {facts.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-muted-foreground">{humanise(key)}</dt>
            <dd>
              {key === 'address_site_id' && typeof value === 'string' ? (
                <EntityLink kind="site" id={value}>
                  Open the Catalog Site at this address
                </EntityLink>
              ) : (
                factText(value)
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Suggestion({ suggestion }: { suggestion: IdentitySuggestion | null }) {
  if (!suggestion)
    return (
      <p className="text-xs text-muted-foreground">No AI suggestion for this Link Candidate.</p>
    );
  return (
    <section aria-label="AI suggestion" className="rounded-3xl border border-dashed p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">AI suggests</span>
        <Badge variant={suggestion.verdict === 'same' ? 'default' : 'secondary'}>
          {suggestion.verdict ? VERDICT_TEXT[suggestion.verdict] : 'no verdict'}
        </Badge>
        {suggestion.confidence !== null && (
          <span>{Math.round(suggestion.confidence * 100)}% confident</span>
        )}
      </div>
      {suggestion.reasons.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5">
          {suggestion.reasons.map(reason => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      <div className="mt-1 text-xs text-muted-foreground">
        {suggestion.model} · prompt {suggestion.prompt_version} ·{' '}
        {new Date(suggestion.suggested_at).toLocaleString()}
      </div>
    </section>
  );
}
